import React from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Text, Icon } from '@reservamos/elements';
import PropTypes from 'prop-types';

/**
 * RucInput component.
 *
 * This component handles RUC input with validation and error display.
 *
 * @param {object} props - rucNumber, onRucChange, onRucValidation, errorMessage, isDisabled, isValidatingRuc
 * @param {string} props.rucNumber - Current RUC number value
 * @param {Function} props.onRucChange - Function to handle RUC input changes
 * @param {Function} props.onRucValidation - Function to handle RUC validation
 * @param {string} props.errorMessage - Error message to display
 * @param {boolean} props.isDisabled - Whether the input is disabled
 * @param {boolean} props.isValidatingRuc - Whether RUC validation is in progress
 *
 * @returns {JSX.Element} The rendered RUC input component.
 */
const RucInput = ({
  rucNumber,
  onRucChange,
  onRucValidation,
  errorMessage,
  isDisabled,
  isValidatingRuc,
}) => {
  const { t } = useTranslation('exchange');

  return (
    <div className="space-y-2">
      <div className="w-full">
        <Input
          id="ruc-input"
          type="text"
          placeholder={t('modal_confirm_billing.ruc_placeholder')}
          value={rucNumber}
          onChange={onRucChange}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && rucNumber.length === 11) {
              onRucValidation(rucNumber);
            }
          }}
          maxLength={11}
          disabled={isDisabled || isValidatingRuc}
        />
      </div>

      <div
        className={`transition-all duration-300 ease-in-out ${
          errorMessage ? 'opacity-100 max-h-20' : 'opacity-0 max-h-0 overflow-hidden'
        }`}
      >
        {errorMessage && (
          <div className="flex items-center">
            <Icon type="CircleX" size="XS" color="error" className="mr-2" />
            <Text size="S" color="error">
              {errorMessage}
            </Text>
          </div>
        )}
      </div>

      {isValidatingRuc && (
        <div className="flex items-center">
          <Icon type="Loader" size="XS" className="mr-2 animate-spin" />
          <Text size="S" color="secondary">
            {t('modal_confirm_billing.searching_taxpayer')}
          </Text>
        </div>
      )}
    </div>
  );
};

RucInput.propTypes = {
  rucNumber: PropTypes.string.isRequired,
  onRucChange: PropTypes.func.isRequired,
  onRucValidation: PropTypes.func.isRequired,
  errorMessage: PropTypes.string,
  isDisabled: PropTypes.bool,
  isValidatingRuc: PropTypes.bool,
};

RucInput.defaultProps = {
  errorMessage: '',
  isDisabled: false,
  isValidatingRuc: false,
};

export default RucInput;
